import { ObjectId } from "@novorender/data-js-api";
import { createContext, Dispatch, MutableRefObject } from "react";

import { VecRGBA } from "utils/color";
import { SelectionSet } from "utils/selectionSet";

// Highlighted/hidden/objectgroups may end up having huge (1M+) collections of objectIds and receive a lot of back-to-back state updates.
// Keeping this state in the redux store ended up slowing down the app too much so we keep it in React contexts instead for now.
//
// Ids are held once, in an immutable SelectionSet (sorted Uint32Array, ~4
// bytes/id). The previous Record<ObjectId, true> + ObjectId[] pair cost ~23
// bytes/id (28 MB measured on a 1.25M-id select-all) and was fully copied on
// every update. `idArr` stays in the state shape as the set's sorted backing
// array so iterating consumers keep working; it MUST NOT be mutated.

export const initialState = {
    ids: SelectionSet.EMPTY,
    idArr: SelectionSet.EMPTY.sortedIds,
    color: [1, 0, 0, 1] as VecRGBA,
};

export type State = typeof initialState;

enum ActionTypes {
    Add,
    Remove,
    SetIds,
    SetColor,
    ResetColor,
    Set,
    RemoveAndAdd,
}

function add(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Add as const,
        ids,
    };
}

function remove(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Remove as const,
        ids,
    };
}

function setIds(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.SetIds as const,
        ids,
    };
}

function setColor(color: State["color"]) {
    return {
        type: ActionTypes.SetColor as const,
        color,
    };
}

function resetColor() {
    return {
        type: ActionTypes.ResetColor as const,
    };
}

function set(payload: { color: State["color"]; ids: Iterable<ObjectId> }) {
    return {
        type: ActionTypes.Set as const,
        payload,
    };
}

function removeAndAdd(payload: { toRemove: Iterable<ObjectId>; toAdd: Iterable<ObjectId> }) {
    return {
        type: ActionTypes.RemoveAndAdd as const,
        payload,
    };
}

export const actions = { add, remove, setIds, setColor, resetColor, set, removeAndAdd };

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchHighlighted = Dispatch<Actions>;

/** True when the action would add, remove, or replace highlighted object IDs. */
export function isHighlightIdMutation(action: Actions): boolean {
    switch (action.type) {
        case ActionTypes.Add:
        case ActionTypes.Remove:
        case ActionTypes.SetIds:
        case ActionTypes.Set:
        case ActionTypes.RemoveAndAdd:
            return true;
        default:
            return false;
    }
}
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyStateContext = createContext<LazyState>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchHighlighted>(undefined as any);

function withIds(ids: SelectionSet, color: State["color"]): State {
    return { ids, idArr: ids.sortedIds, color };
}

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Add: {
            return withIds(state.ids.withAdded(action.ids), state.color);
        }
        case ActionTypes.Remove: {
            return withIds(state.ids.withRemoved(action.ids), state.color);
        }
        case ActionTypes.SetIds: {
            return withIds(SelectionSet.fromIds(action.ids), state.color);
        }
        case ActionTypes.SetColor: {
            return {
                color: action.color,
                ids: state.ids,
                idArr: state.idArr,
            };
        }
        case ActionTypes.ResetColor: {
            return {
                color: initialState.color,
                ids: state.ids,
                idArr: state.idArr,
            };
        }
        case ActionTypes.Set: {
            return withIds(SelectionSet.fromIds(action.payload.ids), action.payload.color);
        }
        case ActionTypes.RemoveAndAdd: {
            return withIds(state.ids.withRemovedAndAdded(action.payload.toRemove, action.payload.toAdd), state.color);
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
